import { ExceptionTypeEnum } from "@/enum/exception-list";
import {
  REASON,
  TAX_OPTIONS,
  UF_OPTIONS,
} from "@/pages/tax/exception-tax/utils/utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "components/ui/form";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { formatStandartRate } from "@/pages/tax/exception-tax/utils/utils";
import { SelectDate } from "../select-date";
import { ModernCard } from "@/pages/tax/exception-tax/components/modern-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { HelpCircle } from "lucide-react";

export const ExceptionFormFields = ({ form }: { form: any }) => {
  const exceptionType = form.watch("exception_type");
  const showReason = exceptionType === ExceptionTypeEnum.ICMS_DESONERADO;
  const showClassTrib =
    exceptionType === ExceptionTypeEnum.IBS ||
    exceptionType === ExceptionTypeEnum.CBS;

  return (
    <div className="w-full flex flex-col">
      <ModernCard
        title="Informações Básicas"
        description="Dados principais da exceção tributária"
      >
        <div
          className={`grid gap-4 ${
            showReason
              ? "grid-cols-1 md:grid-cols-4"
              : "grid-cols-1 md:grid-cols-3"
          }`}
        >
          <FormField
            control={form.control}
            name="rule_name"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  Nome da Exceção
                </Label>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Digite o nome da exceção"
                    className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="exception_type"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  Tributo
                </Label>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full">
                      <SelectValue placeholder="Selecione o tributo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TAX_OPTIONS.map((tax) => (
                      <SelectItem key={tax.value} value={tax.value}>
                        {tax.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {showReason && (
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                    Motivo
                  </Label>
                  <Select
                    defaultValue={field.value || "9"}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full">
                        <SelectValue placeholder="Selecione o motivo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REASON.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  UF
                </Label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full">
                      <SelectValue placeholder="Selecione uma UF" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {UF_OPTIONS.map((uf) => (
                      <SelectItem key={uf} value={uf}>
                        {uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </ModernCard>

      <ModernCard
        className="mt-5"
        title="Período de Vigência"
        description="Defina o período em que a exceção estará ativa"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="initial_term"
            render={({ field }) => (
              <FormItem>
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  Data Inicial da Vigência
                </Label>
                <SelectDate
                  value={field.value}
                  onChange={field.onChange}
                  id={""}
                  label={""}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="final_term"
            render={({ field }) => (
              <FormItem>
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  Data Final da Vigência
                </Label>
                <SelectDate
                  value={field.value}
                  onChange={field.onChange}
                  id={""}
                  label={""}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </ModernCard>

      <ModernCard
        className="mt-5"
        title="Configurações Fiscais"
        description="Parâmetros tributários e códigos fiscais"
      >
        <div
          className={`grid grid-cols-1 md:grid-cols-2  gap-6 ${
            showClassTrib ? "lg:grid-cols-3" : "lg:grid-cols-5"
          }`}
        >
          {showClassTrib && (
            <FormField
              control={form.control}
              name="classification_tax"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <div className="flex items-center gap-2 ">
                    <TooltipProvider>
                      <div className="flex items-center gap-2  relative z-[100]">
                        <Label className="text-sm font-semibold text-gray-800">
                          Classificação Tributária
                        </Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className="cursor-help">
                              <HelpCircle className="h-4 w-4 text-gray-500 hover:text-gray-700 transition-colors" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs bg-[#F5A524] text-gray-100 font-semibold p-3 rounded-lg shadow-lg z-[9999]"
                          >
                            <p className="text-sm">
                              A classificação tributária é obrigatória para os
                              tributos IBS e CBS. Ela define como o produto será
                              tratado para fins de tributação.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  </div>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite a classificação tributária"
                      className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="calculation_basis"
            render={({ field }) => (
              <FormItem className="relative h-[50px]">
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  Base de Cálculo
                </Label>
                <Input
                  {...field}
                  className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full pr-10"
                  value={formatStandartRate(field.value)}
                  onChange={(e) =>
                    field.onChange(formatStandartRate(e.target.value))
                  }
                  placeholder="0%"
                />
                <span className="absolute right-3 top-[35px] text-gray-500 text-sm pointer-events-none">
                  %
                </span>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="exception_rate"
            render={({ field }) => (
              <FormItem className="relative h-[50px]">
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  Alíquota
                </Label>
                <Input
                  {...field}
                  className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full pr-10"
                  value={formatStandartRate(field.value)}
                  onChange={(e) =>
                    field.onChange(formatStandartRate(e.target.value))
                  }
                  placeholder="0%"
                />
                <span className="absolute right-3 top-[35px] text-gray-500 text-sm pointer-events-none">
                  %
                </span>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cfop"
            render={({ field }) => (
              <FormItem>
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  CFOP
                </Label>
                <Input
                  {...field}
                  className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                  placeholder="CFOP"
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cst"
            render={({ field }) => (
              <FormItem>
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  CST
                </Label>
                <Input
                  {...field}
                  className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                  placeholder="CST"
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cest"
            render={({ field }) => (
              <FormItem>
                <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                  CEST
                </Label>
                <Input
                  {...field}
                  className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                  placeholder="CEST"
                  maxLength={7}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </ModernCard>

      <ModernCard className="mt-5" title="Descrição" description="">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <Label className="text-sm font-semibold text-gray-800 mb-2 block">
                Descrição
              </Label>
              <Input
                {...field}
                className="h-12 border-border focus:border-[#F5A524] focus:ring-[#F5A524] w-full"
                placeholder="Descrição da exceção"
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </ModernCard>
    </div>
  );
};
